import i18next from 'i18next';

/**
 * Marketplace money formatting.
 *
 * Salons on Salunas are not all euro-denominated (a Brazilian tenant prices in
 * BRL), so every price on the marketplace must be rendered with the salon's own
 * currency — which the API now exposes as a top-level `currency` (ISO 4217) on
 * salon profiles, search rows and my-bookings rows. This helper is the single
 * place that turns (amount, currency) into a localized string, so no component
 * hardcodes "€" again.
 *
 * Note: `salon.prepayment.currency` is the *online payment* currency and is
 * deliberately NOT what this helper reads — prepay amounts keep using it.
 */

const DEFAULT_CURRENCY = 'EUR';
const DEFAULT_LOCALE = 'en';

export interface FormatPriceOptions {
  /**
   * Fixed number of fraction digits. When omitted, whole amounts render with
   * no decimals ("R$ 90", "€30") and fractional amounts with two ("€12.50").
   */
  fractionDigits?: number;
}

function toNumber(amount: number | string | null | undefined): number {
  if (amount == null || amount === '') return 0;
  const n = typeof amount === 'number' ? amount : Number(amount);
  return Number.isFinite(n) ? n : 0;
}

function normalizeCurrency(currency?: string | null): string {
  const code = (currency ?? '').trim().toUpperCase();
  return code || DEFAULT_CURRENCY;
}

function resolveLocale(locale?: string | null): string {
  if (locale) return locale;
  const lng = i18next.language;
  return lng || DEFAULT_LOCALE;
}

/**
 * Format a price with the salon's currency. Never throws: an unknown currency
 * code or an unsupported locale degrades to "<CODE> <amount>".
 */
export function formatPrice(
  amount: number | string | null | undefined,
  currency?: string | null,
  locale?: string | null,
  opts: FormatPriceOptions = {}
): string {
  const value = toNumber(amount);
  const code = normalizeCurrency(currency);
  const digits = opts.fractionDigits ?? (Number.isInteger(value) ? 0 : 2);

  try {
    return new Intl.NumberFormat(resolveLocale(locale), {
      style: 'currency',
      currency: code,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value);
  } catch {
    return `${code} ${value.toFixed(digits)}`;
  }
}
